// ⌘K: go to a mode (with its key shown: `1`–`6`, or a `g` chord), a Setup page, or another
// company. A mode lands where the playhead says (`modeLanding`), like the tabs. The search
// box in the top bar is the second trigger (design spec 2026-10-19 §2.1). In a company the
// palette also searches the company (`search`; SEARCH-NOTIF: palette-hits.tsx — people, digital
// employees, seats, teams, metrics, priorities, boards, docs, channels, tasks and messages) and
// offers commands ("Invite person", "New seat", …: pages in the "Commands" group; PARITY-0: they
// come from the command registry, command-registry.ts).
import {
  Command,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandShortcut,
  SearchButton,
} from "@companyos/ui";
import {
  ArrowRightLeftIcon,
  LandmarkIcon,
  type LucideIcon,
  SettingsIcon,
  StarIcon,
} from "lucide-react";
import { type ReactNode, useCallback, useEffect, useRef, useState } from "react";
import type { CompanyAccess } from "../features/company";
import { useShellState } from "./shell-state";

/**
 * A page in the palette; `group` is its heading (default "Setup"; M5-04 adds "Finance");
 * `keywords` are more words it is found by (SETUP-REGROUP: its description and old names).
 */
export type CommandPage = {
  key: string;
  label: string;
  run: () => void;
  group?: string;
  /** SEARCH-NOTIF: the row's type icon (a Setup page, a command). */
  icon?: LucideIcon;
  keywords?: string;
};

/** A mode (or the operator console) in the palette, with the key that opens it (`4`, `g p`). */
export type CommandMode = { key: string; label: string; keys: string; run: () => void };

export function CommandMenu({
  modes,
  pages = [],
  companies,
  onCompany,
  compact = false,
  search,
  lead,
}: {
  /**
   * WOS-BUNDLE: rows shown before the modes (the server's recents on an empty query, the open
   * task's link commands); `pick` closes the palette, then runs.
   */
  lead?: (query: string, pick: (fn: () => void) => void) => ReactNode;
  /** The typed query's hits (messages and tasks); `pick` closes the palette, then runs. */
  search?: (query: string, pick: (fn: () => void) => void) => ReactNode;
  modes: readonly CommandMode[];
  /** Beside the mode tabs: the small search box. */
  compact?: boolean;
  /** Setup pages (managers only). */
  pages?: readonly CommandPage[];
  companies: readonly CompanyAccess[];
  onCompany: (slug: string) => void;
}) {
  const { commandOpen, setCommandOpen } = useShellState();
  // Where focus was before the palette opened; it goes back there on close (Escape or pick).
  const opener = useRef<HTMLElement | null>(null);
  const [query, setQuery] = useState("");

  const open = useCallback(() => {
    opener.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setCommandOpen(true);
  }, [setCommandOpen]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key.toLowerCase() === "k" && (event.metaKey || event.ctrlKey) && !event.shiftKey) {
        event.preventDefault();
        open();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open]);

  const restoreFocus = (isOpen: boolean) => {
    if (isOpen) return;
    setQuery("");
    const target = opener.current;
    opener.current = null;
    // A pick may have navigated and unmounted the opener; then focus stays where routing put it.
    if (
      target?.isConnected &&
      (document.activeElement === document.body || !document.activeElement)
    ) {
      target.focus();
    }
  };

  const run = (fn: () => void) => {
    setCommandOpen(false);
    fn();
  };

  return (
    <>
      <SearchButton
        compact={compact}
        onClick={open}
        aria-label="Search and jump (Command K)"
        aria-keyshortcuts="Meta+K Control+K"
      />
      <CommandDialog
        open={commandOpen}
        onOpenChange={setCommandOpen}
        onOpenChangeComplete={restoreFocus}
        title="Go to"
        description={
          search
            ? "Jump to a mode, a Setup page or another company, run a command, or find people, agents, seats, metrics, work and messages."
            : "Jump to a mode, a Setup page or another company."
        }
      >
        <Command label="Go to">
          <CommandInput
            placeholder={search ? "Search people, agents, seats, metrics, work…" : "Go to…"}
            value={query}
            onValueChange={setQuery}
          />
          <CommandList>
            {/* cmdk counts only the items it filters; the palette's hits (forceMount, `data-kind`)
                are the server's matches, so "No match." hides while there are any. */}
            <CommandEmpty className="[div:has([data-kind])>&]:hidden">No match.</CommandEmpty>
            {lead ? lead(query, run) : null}
            {modes.length > 0 ? (
              <CommandGroup heading="Modes">
                {modes.map((m) => (
                  <CommandItem key={m.key} onSelect={() => run(m.run)}>
                    {m.label}
                    <CommandShortcut>{m.keys}</CommandShortcut>
                  </CommandItem>
                ))}
              </CommandGroup>
            ) : null}
            {[...new Set(pages.map((p) => p.group ?? "Setup"))].map((group) => (
              <CommandGroup key={group} heading={group}>
                {pages
                  .filter((p) => (p.group ?? "Setup") === group)
                  .map((p) => {
                    const Icon =
                      p.icon ??
                      (group === "Setup"
                        ? SettingsIcon
                        : group === "Finance"
                          ? LandmarkIcon
                          : null);
                    return (
                      <CommandItem
                        key={p.key}
                        value={`${group} ${p.label}${p.keywords ? ` ${p.keywords}` : ""}`}
                        onSelect={() => run(p.run)}
                      >
                        {Icon ? <Icon aria-hidden="true" /> : null}
                        {p.label}
                      </CommandItem>
                    );
                  })}
              </CommandGroup>
            ))}
            {search ? search(query, run) : null}
            {/* FOUNDER-OS: "Switch to …", the founder's Founder OS first. */}
            <CommandGroup heading="Companies">
              {[...companies]
                .sort((a, b) => Number(b.kind === "portfolio") - Number(a.kind === "portfolio"))
                .map((c) => (
                  <CommandItem
                    key={c.id}
                    value={`Switch to ${c.name} ${c.slug}`}
                    onSelect={() => run(() => onCompany(c.slug))}
                  >
                    {c.kind === "portfolio" ? (
                      <StarIcon aria-hidden="true" />
                    ) : (
                      <ArrowRightLeftIcon aria-hidden="true" />
                    )}
                    Switch to {c.name}
                  </CommandItem>
                ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </CommandDialog>
    </>
  );
}
